"use client"

import React from "react"
import { motion, AnimatePresence } from "framer-motion"
import { BarChart2, TrendingUp, Mail, Eye, MousePointer2, Ban } from "lucide-react"

interface CampaignRowProps {
  name: string
  delivered: number
  opened: number
  clicked: number
  bounced: number
  index: number
}

const CampaignRow = ({ name, delivered, opened, clicked, bounced, index }: CampaignRowProps) => {
  const total = delivered + bounced
  const openRate = delivered > 0 ? (opened / delivered) * 100 : 0
  const clickRate = delivered > 0 ? (clicked / delivered) * 100 : 0

  return (
    <motion.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 + index * 0.05 }}
      className="group border-b border-white/5 hover:bg-white/[0.02] transition-colors"
    >
      <td className="py-4 px-4">
        <div className="flex items-center gap-3">
          <div className="h-2 w-2 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.5)]" />
          <span className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors">{name}</span>
        </div>
      </td>
      <td className="py-4 px-4">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-bold text-white">{delivered.toLocaleString()}</span>
          <div className="h-1 w-16 bg-slate-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
              className="h-full bg-blue-500"
            />
          </div>
        </div>
      </td>
      <td className="py-4 px-4 text-center">
        <div className="inline-flex flex-col items-center">
          <span className="text-sm font-bold text-purple-400">{openRate.toFixed(1)}%</span>
          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">{opened.toLocaleString()}</span>
        </div>
      </td>
      <td className="py-4 px-4 text-center">
        <div className="inline-flex flex-col items-center">
          <span className="text-sm font-bold text-emerald-400">{clickRate.toFixed(1)}%</span>
          <span className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">{clicked.toLocaleString()}</span>
        </div>
      </td>
      <td className="py-4 px-4 text-right">
        <span className="text-sm font-bold text-rose-500/80">{bounced.toLocaleString()}</span>
      </td>
    </motion.tr>
  )
}

interface ModernCampaignTableProps {
  campaigns: Array<{ name: string; delivered: number; opened: number; clicked: number; bounced: number }>
}

const ModernCampaignTable = ({ campaigns }: ModernCampaignTableProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="rounded-3xl border border-white/10 bg-slate-900/40 backdrop-blur-3xl overflow-hidden shadow-2xl h-full flex flex-col"
    >
      <div className="p-6 border-b border-white/10 flex items-center justify-between bg-white/[0.01]">
        <div>
          <h3 className="text-lg font-bold text-white tracking-tight flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-indigo-400" />
            Campaign Performance
          </h3>
          <p className="text-xs text-slate-500 font-semibold uppercase tracking-widest mt-1">Detailed per-campaign analytics breakdown</p>
        </div>
        <button className="p-2 rounded-xl border border-white/10 bg-white/5 text-slate-400 hover:text-white transition-colors">
          <TrendingUp className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-auto custom-scrollbar min-h-[300px]">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-white/[0.02] border-b border-white/10">
              <th className="py-4 px-4 text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">Campaign Name</th>
              <th className="py-4 px-4 text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em]">Delivered</th>
              <th className="py-4 px-4 text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] text-center">Open Rate</th>
              <th className="py-4 px-4 text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] text-center">Click Rate</th>
              <th className="py-4 px-4 text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] text-right">Bounced</th>
            </tr>
          </thead>
          <tbody>
            <AnimatePresence>
              {campaigns && campaigns.length > 0 ? (
                campaigns.map((campaign, index) => (
                  <CampaignRow key={campaign.name} {...campaign} index={index} />
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="py-20 text-center">
                    <div className="flex flex-col items-center gap-3 opacity-20">
                      <Ban className="h-12 w-12 text-slate-500" />
                      <span className="text-xs font-bold uppercase tracking-widest">No campaign data available</span>
                    </div>
                  </td>
                </tr>
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
    </motion.div>
  )
}

export default ModernCampaignTable
